/**
 * Universal Fetch Hook System
 *
 * Provides typed, unified data fetching with error handling, toast notifications,
 * and exceptional developer experience.
 */

import type {
	FetchHookConfig,
	HttpMethod,
	RequestOptions,
} from "@shared/types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

// ===== Core useFetch Hook =====

interface FetchState<T> {
	data: T | null;
	error: ApiError | null;
	loading: boolean;
	refetch: () => Promise<void>;
}

interface FetchOptions<T> extends FetchHookConfig {
	onSuccess?: (data: T) => void;
	onError?: (error: ApiError) => void;
	showSuccessToast?: boolean;
	showErrorToast?: boolean;
	successMessage?: string;
	parseResponse?: (response: Response) => Promise<T>;
}

/**
 * Universal fetch hook with comprehensive error handling and type safety
 */
export function useFetch<T>(
	url: string | null,
	method: HttpMethod = "GET",
	options: FetchOptions<T> = {},
): FetchState<T> {
	const {
		enabled = true,
		timeout = 10000,
		retry = 3,
		retryDelay = 1000,
		headers = {},
		onSuccess,
		onError,
		showSuccessToast = false,
		showErrorToast = true,
		successMessage,
		parseResponse,
		signal,
	} = options;

	const [state, setState] = useState<Omit<FetchState<T>, "refetch">>({
		data: null,
		error: null,
		loading: false,
	});

	// Use refs for callback functions to prevent dependency changes
	const onSuccessRef = useRef(onSuccess);
	const onErrorRef = useRef(onError);
	const parseResponseRef = useRef(parseResponse);
	
	// Update refs on every render
	onSuccessRef.current = onSuccess;
	onErrorRef.current = onError;
	parseResponseRef.current = parseResponse;

	// Memoize headers to prevent unnecessary re-renders
	const stableHeaders = useMemo(() => headers, [JSON.stringify(headers)]);

	const fetchData = useCallback(
		async (retryCount = 0): Promise<void> => {
			if (!url || !enabled) return;

			setState((prev) => ({ ...prev, loading: true, error: null }));

			try {
				const controller = new AbortController();
				const timeoutId = setTimeout(() => controller.abort(), timeout);

				const combinedSignal = signal || controller.signal;

				const response = await fetch(url, {
					method,
					headers: {
						"Content-Type": "application/json",
						...stableHeaders,
					},
					credentials: "include",
					signal: combinedSignal,
				});

				clearTimeout(timeoutId);

				let data: T;

				if (parseResponseRef.current) {
					data = await parseResponseRef.current(response);
				} else {
					const responseData = await response.json();

					if (!response.ok) {
						throw new ApiError(
							responseData.error || `HTTP ${response.status}`,
							responseData.message || response.statusText,
							responseData.details,
							response.status,
							response.statusText,
						);
					}

					data = responseData;
				}

				setState({ data, error: null, loading: false });

				if (showSuccessToast && successMessage) {
					toast.success(successMessage);
				}

				onSuccessRef.current?.(data);
			} catch (error) {
				const apiError =
					error instanceof ApiError
						? error
						: new ApiError(
								"Network Error",
								error instanceof Error
									? error.message
									: "Unknown error occurred",
								undefined,
								0,
								"Network Error",
							);

				// Retry logic
				if (retryCount < (typeof retry === "number" ? retry : retry ? 3 : 0)) {
					setTimeout(() => {
						fetchData(retryCount + 1);
					}, retryDelay * Math.pow(2, retryCount)); // Exponential backoff
					return;
				}

				setState({ data: null, error: apiError, loading: false });

				if (showErrorToast) {
					toast.error(apiError.error, {
						description: apiError.message,
					});
				}

				onErrorRef.current?.(apiError);
			}
		},
		[
			url,
			method,
			enabled,
			timeout,
			retry,
			retryDelay,
			stableHeaders,
			showSuccessToast,
			showErrorToast,
			successMessage,
			signal,
		],
	);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	return {
		...state,
		refetch: fetchData,
	};
}

// ===== Custom ApiError Class =====

export class ApiError extends Error {
	constructor(
		public readonly error: string,
		message?: string,
		public readonly details?: string,
		public readonly status?: number,
		public readonly statusText?: string,
	) {
		super(message || error);
		this.name = "ApiError";
		this.message = message || error;
	}
}

// ===== Specialized HTTP Method Hooks =====

/**
 * GET request hook
 */
export function useGet<T>(
	url: string | null,
	options: Omit<FetchOptions<T>, "method"> = {},
): FetchState<T> {
	return useFetch(url, "GET", options);
}

/**
 * POST request hook (manual trigger only)
 */
export function usePost<TRequest, TResponse>(
	url: string,
	options: Omit<FetchOptions<TResponse>, "method"> = {},
): {
	post: (data: TRequest) => Promise<TResponse | null>;
	loading: boolean;
	error: ApiError | null;
	data: TResponse | null;
} {
	const mutation = useMutation<TRequest, TResponse>(url, "POST", {
		showSuccessToast: options.showSuccessToast,
		showErrorToast: options.showErrorToast,
		successMessage: options.successMessage,
		onSuccess: options.onSuccess,
		onError: options.onError,
		...options,
	});

	return {
		post: mutation.mutate,
		loading: mutation.loading,
		error: mutation.error,
		data: mutation.data,
	};
}

/**
 * PUT request hook (manual trigger only)
 */
export function usePut<TRequest, TResponse>(
	url: string,
	options: Omit<FetchOptions<TResponse>, "method"> = {},
): {
	put: (data: TRequest) => Promise<TResponse | null>;
	loading: boolean;
	error: ApiError | null;
	data: TResponse | null;
} {
	const mutation = useMutation<TRequest, TResponse>(url, "PUT", {
		showSuccessToast: options.showSuccessToast,
		showErrorToast: options.showErrorToast,
		successMessage: options.successMessage,
		onSuccess: options.onSuccess,
		onError: options.onError,
		...options,
	});

	return {
		put: mutation.mutate,
		loading: mutation.loading,
		error: mutation.error,
		data: mutation.data,
	};
}

/**
 * DELETE request hook (manual trigger only)
 */
export function useDelete<TResponse = any>(
	url: string,
	options: Omit<FetchOptions<TResponse>, "method"> = {},
): {
	delete: () => Promise<TResponse | null>;
	loading: boolean;
	error: ApiError | null;
	data: TResponse | null;
} {
	const mutation = useMutation<{}, TResponse>(url, "DELETE", {
		showSuccessToast: options.showSuccessToast,
		showErrorToast: options.showErrorToast,
		successMessage: options.successMessage,
		onSuccess: options.onSuccess,
		onError: options.onError,
		...options,
	});

	const deleteResource = useCallback(() => {
		return mutation.mutate({});
	}, [mutation]);

	return {
		delete: deleteResource,
		loading: mutation.loading,
		error: mutation.error,
		data: mutation.data,
	};
}

/**
 * PATCH request hook (manual trigger only)
 */
export function usePatch<TRequest, TResponse>(
	url: string,
	options: Omit<FetchOptions<TResponse>, "method"> = {},
): {
	patch: (data: TRequest) => Promise<TResponse | null>;
	loading: boolean;
	error: ApiError | null;
	data: TResponse | null;
} {
	const mutation = useMutation<TRequest, TResponse>(url, "PATCH", {
		showSuccessToast: options.showSuccessToast,
		showErrorToast: options.showErrorToast,
		successMessage: options.successMessage,
		onSuccess: options.onSuccess,
		onError: options.onError,
		...options,
	});

	return {
		patch: mutation.mutate,
		loading: mutation.loading,
		error: mutation.error,
		data: mutation.data,
	};
}

// ===== Mutation Hook =====

interface MutationState<T> {
	data: T | null;
	error: ApiError | null;
	loading: boolean;
}

interface MutationOptions<TRequest, TResponse> extends RequestOptions {
	onSuccess?: (data: TResponse, variables: TRequest) => void;
	onError?: (error: ApiError, variables: TRequest) => void;
	showSuccessToast?: boolean;
	showErrorToast?: boolean;
	successMessage?: string;
}

/**
 * Mutation hook for manual triggers (not auto-fetching)
 */
export function useMutation<TRequest, TResponse>(
	url: string,
	method: HttpMethod = "POST",
	options: MutationOptions<TRequest, TResponse> = {},
) {
	const [state, setState] = useState<MutationState<TResponse>>({
		data: null,
		error: null,
		loading: false,
	});

	const mutate = useCallback(
		async (variables: TRequest): Promise<TResponse | null> => {
			setState({ data: null, error: null, loading: true });

			try {
				const response = await fetch(url, {
					method,
					headers: {
						"Content-Type": "application/json",
						...options.headers,
					},
					credentials: "include",
					body: JSON.stringify(variables),
					signal: options.signal,
				});

				const responseData = await response.json();

				if (!response.ok) {
					throw new ApiError(
						responseData.error || `HTTP ${response.status}`,
						responseData.message || response.statusText,
						responseData.details,
						response.status,
						response.statusText,
					);
				}

				setState({ data: responseData, error: null, loading: false });

				if (options.showSuccessToast && options.successMessage) {
					toast.success(options.successMessage);
				}

				options.onSuccess?.(responseData, variables);
				return responseData;
			} catch (error) {
				const apiError =
					error instanceof ApiError
						? error
						: new ApiError(
								"Network Error",
								error instanceof Error
									? error.message
									: "Unknown error occurred",
							);

				setState({ data: null, error: apiError, loading: false });

				if (options.showErrorToast !== false) {
					toast.error(apiError.error, {
						description: apiError.message,
					});
				}

				options.onError?.(apiError, variables);
				return null;
			}
		},
		[url, method, JSON.stringify(options)],
	);

	return {
		...state,
		mutate,
	};
}
